import { ActionIcon, Box, Group, Image, Modal, Stack, Text, Tooltip } from '@mantine/core'
import { TbChevronLeft, TbChevronRight, TbX, TbZoomIn, TbZoomOut, TbZoomReset } from 'react-icons/tb'
import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useRef, useState } from 'react'

type PhotoLightboxProps = {
  opened: boolean
  onClose: () => void
  photos: Array<{ name: string; src: string; caption: string }>
  index: number
  onIndexChange: (index: number) => void
}

const MIN_ZOOM = 0.5
const MAX_ZOOM = 6
const ZOOM_STEP = 0.25

const PhotoLightbox = ({ opened, onClose, photos, index, onIndexChange }: PhotoLightboxProps) => {
  const { t } = useTranslation('unexpectedEvents')
  const [zoom, setZoom] = useState(1)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const safeIndex = Math.min(Math.max(index, 0), Math.max(0, photos.length - 1))
  const photo = photos[safeIndex]

  const goPrev = useCallback(() => {
    if (photos.length === 0) return
    onIndexChange((safeIndex - 1 + photos.length) % photos.length)
    setZoom(1)
  }, [photos.length, safeIndex, onIndexChange])

  const goNext = useCallback(() => {
    if (photos.length === 0) return
    onIndexChange((safeIndex + 1) % photos.length)
    setZoom(1)
  }, [photos.length, safeIndex, onIndexChange])

  useEffect(() => {
    if (!opened) return
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        goPrev()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        goNext()
      } else if (event.key === 'Escape') {
        onClose()
      } else if (event.key === '+' || event.key === '=') {
        setZoom(z => Math.min(MAX_ZOOM, +(z + ZOOM_STEP).toFixed(2)))
      } else if (event.key === '-') {
        setZoom(z => Math.max(MIN_ZOOM, +(z - ZOOM_STEP).toFixed(2)))
      } else if (event.key === '0') {
        setZoom(1)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [opened, goPrev, goNext, onClose])

  useEffect(() => {
    if (!opened) setZoom(1)
  }, [opened])

  const handleWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    event.preventDefault()
    setZoom(prev => {
      const delta = event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP
      return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, +(prev + delta).toFixed(2)))
    })
  }

  if (!photo) return null

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="100%"
      withCloseButton={false}
      padding={0}
      styles={{
        body: { padding: 0, background: 'rgba(0,0,0,0.92)' },
        content: { background: 'rgba(0,0,0,0.92)' },
      }}
      fullScreen
    >
      <Stack gap={0} style={{ height: '100vh' }}>
        <Group justify="space-between" px="md" py="sm" style={{ background: 'rgba(0,0,0,0.6)', color: '#fff' }}>
          <Text size="sm" c="white">
            {t('photos.photoOf', { current: safeIndex + 1, total: photos.length })}
          </Text>
          <Group gap="xs">
            <Tooltip label={t('photos.zoomOut')}>
              <ActionIcon
                variant="default"
                onClick={() => setZoom(z => Math.max(MIN_ZOOM, +(z - ZOOM_STEP).toFixed(2)))}
              >
                <TbZoomOut size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={t('photos.resetZoom')}>
              <ActionIcon variant="default" onClick={() => setZoom(1)}>
                <TbZoomReset size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={t('photos.zoomIn')}>
              <ActionIcon
                variant="default"
                onClick={() => setZoom(z => Math.min(MAX_ZOOM, +(z + ZOOM_STEP).toFixed(2)))}
              >
                <TbZoomIn size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={t('photos.close')}>
              <ActionIcon variant="default" onClick={onClose} aria-label={t('photos.close')}>
                <TbX size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>

        <Box
          ref={wrapperRef}
          onWheel={handleWheel}
          style={{
            position: 'relative',
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'auto',
          }}
        >
          <ActionIcon
            variant="filled"
            color="dark"
            size="xl"
            radius="xl"
            onClick={goPrev}
            aria-label={t('photos.previous')}
            style={{ position: 'absolute', left: 16, top: '50%', zIndex: 5 }}
          >
            <TbChevronLeft size={20} />
          </ActionIcon>

          <Image
            src={photo.src}
            alt={photo.name}
            fit="contain"
            style={{
              maxHeight: '85vh',
              maxWidth: '90vw',
              transform: `scale(${zoom})`,
              transformOrigin: 'center center',
              transition: 'transform 0.15s ease-out',
            }}
          />

          <ActionIcon
            variant="filled"
            color="dark"
            size="xl"
            radius="xl"
            onClick={goNext}
            aria-label={t('photos.next')}
            style={{ position: 'absolute', right: 16, top: '50%', zIndex: 5 }}
          >
            <TbChevronRight size={20} />
          </ActionIcon>
        </Box>

        <Box px="md" py="sm" style={{ background: 'rgba(0,0,0,0.6)', color: '#fff' }}>
          <Text size="sm" ta="center" c="white">
            {photo.caption}
          </Text>
        </Box>
      </Stack>
    </Modal>
  )
}

export default PhotoLightbox
